<template>
  <ValidationObserver v-slot="{ handleSubmit }">
    <div class="absolute inset-0 flex flex-col overflow-y-hidden">
      <div class="flex-1 overflow-y-auto">
        <header class="space-y-1 py-6 px-4 bg-cyan-700">
          <div class="flex items-center justify-between">
            <h2 class="text-lg leading-7 font-medium text-white">
              <span v-if="formAction === 'add'">เพิ่มประกาศ</span>
              <span v-if="formAction === 'edit'">แก้ไขประกาศ</span>
            </h2>
            <button
              @click="onClose"
              aria-label="Close panel"
              class="
                text-cyan-200
                hover:text-white
                transition
                ease-in-out
                duration-150
              "
            >
              <svg
                class="h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </div>
          <div>
            <p class="text-sm leading-5 text-cyan-300">
              <span v-if="formAction === 'add'">
                เพิ่มประกาศ เพื่อแจ้งข่าวสารให้กับผู้ใช้งาน
              </span>
              <span v-if="formAction === 'edit'">
                แก้ไขประกาศ เพื่อแจ้งข่าวสารให้กับผู้ใช้งาน
              </span>
            </p>
          </div>
        </header>

        <!-- Error Message -->
        <div
          v-if="errors.length > 0"
          class="px-4 py-3 bg-red-600 bg-opacity-20 flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 text-red-600"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
          <p class="text-base font-semibold text-red-800 flex-1 px-3">
            {{ errors[0] }}
          </p>
          <button @click="errors = []" type="button" class="focus:outline-none">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-red-600"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>
        <!-- End Error Message -->

        <div class="p-4 space-y-6">
          <div>
            <ValidationProvider
              name="หัวข้อ"
              rules="required"
              v-slot="{ errors }"
            >
              <AppInput
                v-model.trim="announcement.title"
                label="หัวข้อ"
                required
                type="text"
                :isDisabled="formAction === 'edit' || isAnnouncementAdded"
              />
              <span class="text-base text-red-600">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div>
            <ValidationProvider
              name="ข้อความ"
              rules="required"
              v-slot="{ errors }"
            >
              <AppTextarea
                v-model="announcement.message"
                label="ข้อความ"
                required
                :isDisabled="formAction === 'edit' || isAnnouncementAdded"
              />
              <span class="text-base text-red-600">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div>
            <DateTimeInput
              v-model="announcement.expired_datetime"
              label="ประกาศหมดอายุวันเวลา"
              :disabled="isAnnouncementAdded"
            />
          </div>

          <div>
            <label class="block text-base font-medium text-gray-700">
              แนบรูป
            </label>
            <div v-if="formAction === 'edit'">
              <div v-if="announcement.system_announcement_image.length === 0">
                No Images
              </div>
              <div class="flex flex-col space-y-4">
                <div
                  v-for="file in announcement.system_announcement_image"
                  :key="file.system_announcement_image_uuid"
                  class="mt-2 border rounded-md overflow-hidden"
                >
                  <img :src="file.image_url" class="w-full h-auto" />
                </div>
              </div>
            </div>
            <div v-else>
              <div class="flex flex-col space-y-4">
                <div
                  v-for="file in selectedFiles"
                  :key="file.uuid"
                  class="relative mt-2 border rounded-md overflow-hidden"
                >
                  <button
                    @click="onDeletedFile(file.uuid)"
                    type="button"
                    class="
                      absolute
                      top-1
                      right-1
                      w-full
                      inline-flex
                      justify-center
                      rounded-md
                      border border-gray-300
                      shadow-sm
                      p-1.5
                      bg-white
                      text-sm
                      font-medium
                      text-gray-700
                      hover:bg-gray-50
                      focus:outline-none
                      focus:ring-2
                      focus:ring-offset-2
                      focus:ring-cyan-500
                      sm:w-auto
                    "
                  >
                    <svg
                      class="h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </button>
                  <img :src="file.image_base64" class="w-full h-auto" />
                </div>
              </div>
              <input
                class="sr-only"
                type="file"
                @change="onFileSelected"
                ref="fileInput"
              />
              <button
                v-show="selectedFiles.length < 3"
                @click="$refs.fileInput.click()"
                type="button"
                class="
                  mt-4
                  w-full
                  inline-flex
                  justify-center
                  rounded-md
                  border border-gray-300
                  shadow-sm
                  px-4
                  py-2
                  bg-white
                  text-sm
                  font-medium
                  text-gray-700
                  hover:bg-gray-50
                  focus:outline-none
                  focus:ring-2
                  focus:ring-offset-2
                  focus:ring-cyan-500
                  sm:w-auto
                "
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="-ml-1 mr-2 h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                    clip-rule="evenodd"
                  />
                </svg>
                เพิ่มรูป
              </button>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div class="p-4 border-t flex justify-end">
          <div class="space-x-4">
            <button
              v-if="
                (!isDataChange && formAction === 'edit') ||
                (isAnnouncementAdded && formAction === 'add')
              "
              @click="onDelete"
              :disabled="loading"
              :class="{ 'cursor-not-allowed': loading }"
              type="button"
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-transparent
                shadow-sm
                px-4
                py-2
                bg-red-600
                text-base
                font-medium
                text-white
                hover:bg-red-700
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-red-500
                sm:w-auto
              "
            >
              <svg
                v-if="loading"
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              <span class="ml-2">ลบ</span>
            </button>
            <button
              v-if="
                (!isAnnouncementAdded && formAction === 'add') ||
                (isDataChange && formAction === 'edit')
              "
              @click="onCancel"
              :disabled="loading"
              :class="{ 'cursor-not-allowed': loading }"
              type="button"
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-gray-300
                shadow-sm
                px-4
                py-2
                bg-white
                text-base
                font-medium
                text-gray-700
                hover:bg-gray-50
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-cyan-500
                sm:ml-3 sm:w-auto
              "
            >
              ยกเลิก
            </button>
            <button
              v-if="formAction === 'add' && !isAnnouncementAdded"
              @click="handleSubmit(onSubmit)"
              :disabled="loading"
              :class="{ 'cursor-not-allowed': loading }"
              type="button"
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-transparent
                shadow-sm
                px-4
                py-2
                bg-cyan-600
                text-base
                font-medium
                text-white
                hover:bg-cyan-700
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-cyan-500
                sm:w-auto
              "
            >
              <svg
                v-if="loading"
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              ตกลง
            </button>
            <button
              v-if="formAction === 'edit' && isDataChange"
              @click="handleSubmit(onSave)"
              :disabled="loading"
              :class="{ 'cursor-not-allowed': loading }"
              type="button"
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-transparent
                shadow-sm
                px-4
                py-2
                bg-cyan-600
                text-base
                font-medium
                text-white
                hover:bg-cyan-700
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-cyan-500
                sm:w-auto
              "
            >
              <svg
                v-if="loading"
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              บันทึก
            </button>

            <button
              v-if="
                isAnnouncementAdded ||
                (formAction === 'edit' &&
                  !announcement.sent_datetime &&
                  !isDataChange)
              "
              @click="onSendAnnouncement"
              type="button"
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-transparent
                shadow-sm
                px-4
                py-2
                bg-cyan-600
                text-base
                font-medium
                text-white
                hover:bg-cyan-700
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-cyan-500
                sm:w-auto
              "
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M16.881 4.345A23.112 23.112 0 0 1 8.25 6H7.5a5.25 5.25 0 0 0-.88 10.427 21.593 21.593 0 0 0 1.378 3.94c.464 1.004 1.674 1.32 2.582.796l.657-.379c.88-.508 1.165-1.593.772-2.468a17.116 17.116 0 0 1-.628-1.607c1.918.258 3.76.75 5.5 1.446A21.727 21.727 0 0 0 18 11.25c0-2.414-.393-4.735-1.119-6.905ZM18.26 3.74a23.22 23.22 0 0 1 1.24 7.51 23.22 23.22 0 0 1-1.41 7.992.75.75 0 1 0 1.409.516 24.555 24.555 0 0 0 1.415-6.43 2.992 2.992 0 0 0 .836-2.078c0-.807-.319-1.54-.836-2.078a24.65 24.65 0 0 0-1.415-6.43.75.75 0 1 0-1.409.516c.059.16.116.321.17.483Z"
                />
              </svg>
              <span class="ml-2">ส่งประกาศ</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </ValidationObserver>
</template>

<script>
import { uuid } from 'uuidv4'
import moment from 'moment'

import AppInput from '@/components/AppInput'
import AppTextarea from '@/components/AppTextarea'
import DateTimeInput from '@/components/DateTimeInput'
import { extend } from 'vee-validate'
import { required } from 'vee-validate/dist/rules'

// No message specified.
// extend('required', required)

// Override the default message.
extend('required', {
  ...required,
  message: 'จำเป็นต้องระบุ {_field_}',
})

export default {
  name: 'FormAnnouncement',

  props: {
    formAction: {
      type: String,
    },
    announcementInit: {
      type: Object,
    },
    announcement: {
      type: Object,
    },
  },

  components: {
    AppInput,
    AppTextarea,
    DateTimeInput,
  },

  data() {
    return {
      loading: false,
      errors: [],
      isAnnouncementAdded: false,
      selectedFiles: [],
    }
  },

  computed: {
    isDataChange() {
      if (
        this.announcementInit.expired_datetime !==
        this.announcement.expired_datetime
      ) {
        this.$emit('isDataChange', true)
        return true
      }
      this.$emit('isDataChange', false)
      return false
    },
  },

  methods: {
    toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = (error) => reject(error)
      })
    },

    onDeletedFile(uuid) {
      this.selectedFiles = this.selectedFiles.filter(
        (file) => file.uuid !== uuid
      )
    },

    async onFileSelected(event) {
      const file = event.target.files[0]
      if (!file) return

      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Only images are allowed!')
        return
      }

      // Validate file size (e.g., max 1MB)
      if (file.size > 1 * 1024 * 1024) {
        alert('File size exceeds 1MB!')
        return
      }

      try {
        const base64 = await this.toBase64(file)

        this.selectedFiles.push({
          uuid: uuid(),
          image_base64: base64,
          image_extension: `.${file.name.split('.').pop()}`,
        })
      } catch (error) {
        console.error('Error converting file to Base64', error)
      }
    },

    onCancel() {
      this.$emit('onCancel')
    },

    onClose() {
      this.$emit('onClose')
    },

    onSendAnnouncement() {
      this.$emit('onSendAnnouncement')
    },

    async onSubmit() {
      try {
        this.loading = true

        const newAnnouncement = {
          title: this.announcement.title,
          message: this.announcement.message
            .split('\n') // Split text into lines
            .map((line) => line.trimEnd()) // Trim right spaces for each line
            .join('\n'), // Join lines back,
          expired_datetime: this.announcement.expired_datetime
            ? moment(
                this.announcement.expired_datetime,
                'DD/MM/YYYY HH:mm'
              ).format()
            : null,
          system_announcement_image: this.selectedFiles.length
            ? this.selectedFiles.map((file, index) => ({
                ...file,
                image_base64: file.image_base64.split(',')[1],
                display_order: index + 1,
              }))
            : null,
        }

        const data = await this.$store.dispatch(
          'announcement/addAnnouncement',
          newAnnouncement
        )

        const notification = {
          type: 'success',
          message: 'Your announcement has been added!',
        }
        this.$store.dispatch('notification/add', notification)

        this.$emit('isDataChange', false)
        this.$emit('updateAnnouncement', data)
        this.isAnnouncementAdded = true
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors.push(
            error.response.data.message || 'Something went wrong.'
          )
        }

        console.log(error.response)
      } finally {
        this.loading = false
      }
    },

    async onSave() {
      try {
        this.loading = true

        const announcement = {
          system_announcement_uuid: this.announcement.system_announcement_uuid,
          expired_datetime: this.announcement.expired_datetime
            ? moment(
                this.announcement.expired_datetime,
                'DD/MM/YYYY HH:mm'
              ).format()
            : null,
        }

        await this.$store.dispatch(
          'announcement/editAnnouncement',
          announcement
        )

        const notification = {
          type: 'success',
          message: 'Your announcement has been updated!',
        }
        this.$store.dispatch('notification/add', notification)

        this.$emit('isDataChange', false)
        this.onClose()
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors.push(
            error.response.data.message || 'Something went wrong.'
          )
        }

        console.log(error.response)
      } finally {
        this.loading = false
      }
    },

    onDelete() {
      this.$emit('onDelete', this.announcement)
    },
  },
}
</script>
